import debounce from 'lodash/debounce';
import {
  type ChangeEventHandler,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {searchGuests} from '~/ViewInvitation/components/Guestlist/actions/actions';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {SearchLineIcon} from '~library/svg/SearchLineIcon';
import {XIcon} from '~library/svg/v2/XIcon';

import './SearchInput.scss';

type SearchFn = (q: string) => void;

export const SearchInput = memo(() => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncing, setDebouncing] = useState(false);
  const {dispatch} = useGuestlistDataContext();

  const doSearch = useRef(
    debounce<SearchFn>((q: string) => {
      searchGuests(dispatch, q);
      setDebouncing(false);
    }, 300)
  ).current;

  useEffect(() => {
    return () => doSearch('');
  }, [doSearch]);

  const setSearchValue = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setDebouncing(true);
      doSearch(query);
    },
    [doSearch]
  );

  const onSearchInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setSearchValue(e.currentTarget.value);
    },
    [setSearchValue]
  );

  const endAdornment = useMemo(() => {
    if (debouncing) return <span className="evt-spinner inline" />;
    if (searchQuery) {
      return (
        <Button
          className="search-input__clear-button"
          variant="transparent"
          data-qa-id="clear-search-button"
          onClick={() => setSearchValue('')}
        >
          <XIcon ratio={0.5} />
        </Button>
      );
    }
  }, [debouncing, searchQuery, setSearchValue]);

  return (
    <Input
      type="text"
      id="guest-search"
      maxLength={50}
      data-qa-id="guest-search-input"
      className="guest-manage__search-input"
      placeholder="Search guests"
      value={searchQuery}
      onChange={onSearchInputChange}
      startAdornment={
        <Button
          onClick={() => setSearchQuery(searchQuery)}
          variant="transparent"
          data-qa-id="button-find-guests"
          title="perform search"
        >
          <SearchLineIcon color="pebble" ratio={0.5} />
        </Button>
      }
      endAdornment={endAdornment}
    />
  );
});

SearchInput.displayName = 'SearchInput';
